/**
 * `external-sharing` Phase 6 Session 6.1 — single node in the version graph.
 *
 * Represents one conversation in the fork chain. Click → navigates to the
 * Builder for that conversation (the server-side response gating means the
 * caller has at least read access to every node in the response; the route
 * itself handles 403 as a downstream concern). `isOriginal` nodes get a
 * subtle "ORIGINAL" chip; nodes whose newest child-share is `revoked` get a
 * `data-revoked="true"` attribute + dimmed styling.
 */
import { useNavigate } from '@tanstack/react-router';
import clsx from 'clsx';
import type { VersionGraphNode as VersionGraphNodeType } from '@arcanum-hq/shared-types';
import { formatRelativeTime } from '@/lib/format';

export interface VersionGraphNodeProps {
  node: VersionGraphNodeType;
  isCurrent: boolean;
  onClose: () => void;
}

/**
 * A node is rendered as "revoked-look" when EVERY outbound share originating
 * from this conversation is revoked AND there are no active descendants. We
 * keep this conservative — partial-revocation chains stay full-color so users
 * understand the chain is still navigable.
 */
function isAllChildSharesRevoked(node: VersionGraphNodeType): boolean {
  if (node.childShares.length === 0) return false;
  return node.childShares.every((c) => c.status === 'revoked');
}

export function VersionGraphNode({ node, isCurrent, onClose }: VersionGraphNodeProps) {
  const navigate = useNavigate();
  const allRevoked = isAllChildSharesRevoked(node);

  function handleClick() {
    navigate({
      to: '/builder',
      search: { conversation: node.conversationId, tab: 'workbench' as const },
    });
    onClose();
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid="version-graph-node"
      data-current={isCurrent ? 'true' : 'false'}
      data-revoked={allRevoked ? 'true' : 'false'}
      className={clsx(
        'flex flex-col items-start gap-1 px-4 py-3 rounded-[3px] border text-left',
        'transition-colors hover:bg-panelBg/60 focus:outline-none focus:ring-1 focus:ring-accent',
        isCurrent
          ? 'border-accent bg-accent-dim/20'
          : 'border-panelBorder bg-panelBg',
        allRevoked && 'opacity-50',
      )}
    >
      <div className="flex items-center gap-2 w-full">
        <span className="font-mono text-micro text-text-muted uppercase tracking-[0.5px]">
          {node.ownerName}
        </span>
        {node.isOriginal && (
          <span className="font-mono text-micro text-accent uppercase tracking-[0.5px] px-1.5 py-0.5 border border-accent/30 rounded-[2px]">
            Original
          </span>
        )}
        {isCurrent && (
          <span className="font-mono text-micro text-accent uppercase tracking-[0.5px] ml-auto">
            You are here
          </span>
        )}
      </div>
      <span className="font-body text-sm text-textBright">{node.conversationTitle}</span>
      <span className="font-mono text-micro text-text-muted tabular-nums">
        {formatRelativeTime(node.createdAt)}
      </span>
    </button>
  );
}
